'use client';
import type { LucideIcon } from 'lucide-react';

import { Button } from './Button';

/** A square, icon-only control: the app `Button` at its `icon` size, with the label moved out of the child
 *  and into `aria-label`.
 *
 *  It goes through `./Button.tsx` rather than reaching past it to the primitive, which is what it used to
 *  do. That shortcut cost two things. It spelled shadcn variant names here — a second app→shadcn map, in
 *  which `default` meant `outline` while the same word meant `secondary` one file over — and it asked for
 *  a size and then contradicted it in `className`, so the size axis decided nothing. Now the size axis
 *  supplies the square box and its padding, and the only class left is this app's deliberate corner
 *  deviation from the primitive's `icon` size, for the outline glyph.
 *
 *  That deviation is the corners, not the height: 36px on a desktop and 44px on a coarse pointer, exactly
 *  like the button beside it. A local `size-7` used to shrink it to 28px and left one action row measuring
 *  36/32/28; a group that genuinely needs the compact step takes `size="icon-sm"` through the shared axis.
 *
 *  `danger` is the delete glyph and is the same look everywhere: the `ghost-danger` app variant, so it
 *  rests as a muted borderless icon and turns fully red under the pointer or keyboard focus. It used to be
 *  `outline-danger`, which drew a red square around the glyph at rest — a shape so much louder than the
 *  action that it read as the row's primary control. Every hand-rolled trash button in the app and in the
 *  plugins now comes through here, so this one mapping is what keeps them identical. */
export function IconButton({ icon: Icon, label, onClick, variant = 'default', disabled = false }: { icon: LucideIcon; label: string; onClick?: () => void; variant?: 'default' | 'danger'; disabled?: boolean }) {
  return (
    <Button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      variant={variant === 'danger' ? 'ghost-danger' : 'outline'}
      size="icon"
      icon={Icon}
      // The bordered outline glyph is square, and deliberately so: it aligns to the edge of a cell or a
      // toolbar, where a radius reads as a gap in the rule the row is drawn on. The danger glyph has no edge
      // at rest, so its only shape is the red hover fill, and that keeps the primitive's `rounded-md` like
      // every ghost icon button and kebab trigger it sits beside.
      className={variant === 'danger' ? 'duration-[var(--motion-fast)]' : 'rounded-none duration-[var(--motion-fast)]'}
    />
  );
}
